import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Image } from 'antd';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { stackoverflowDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { ze } from 'utils/zhEn';
import style from './CrashClawDocs.scss';
/** markdown 中相对路径 → webpack 导入后的实际 URL 映射（v2/v3 截图就绪后补充） */
const IMAGE_MAP = {};
/** 从 markdown img src（如 ./static/xxx.png）中提取文件名，查 IMAGE_MAP 返回构建后 URL */
function resolveImageSrc(src) {
    if (!src)
        return '';
    const filename = src.split('/').pop() || '';
    return IMAGE_MAP[filename] || src;
}
const MARKDOWN_CONTENT = `# CrashClaw 配置

CrashClaw 是 CrashSight 的 AI 崩溃分析助手。配置流程已精简为 **工作区 → 连接器 → 技能** 三步，整体配置约 1 分钟即可完成。

入口：[CrashClaw 配置页](https://crashsight.qq.com/account/profile?tag=crashClawConfig)

## 配置流程总览

1. **[工作区](#1-工作区)**：选一种部署形态，启动 Bridge，让 Agent 跑起来
2. **[连接器](#2-连接器)**：按需接入数据源（工蜂 / TAPD）与通知通道（企微 / 邮件 / Webhook）
3. **[技能](#3-技能)**：平台技能开箱即用，也可上传自定义 Skill

> 关于 **Agent**：v2 起 Agent 完全由后端托管，创建工作区时自动绑定默认 Agent，前端不再暴露 Agent 配置入口。详见 [Agent](#4-agent) 一节。

---

## 1. 工作区

工作区是 CrashClaw Agent 的运行环境。点「创建工作区」后，弹窗顶部提供两种部署形态二选一：

### 1）AnyDev 容器（默认 / 推荐）

- 系统自动创建云端容器并部署 CrashClaw Agent
- 自动配置 CrashSight MCP
- 表单字段：
  - **工作区名称**（必填）
  - **描述**（可选）
  - **Agent 类型**：Hermes（推荐）/ CodeBuddy
  - **LLM API Key**（必填）：前往 Venus 个人账号页面获取 secretKey，仅下发到容器 install.sh，不落库
- 点击「**一键创建**」即完成全部部署，新工作区会出现在列表

适用场景：90% 的用户首选此方式，零运维。

### 2）CVM / 开发机（仅 Mac / Linux）

- 在你自己的 CVM 或 Mac/Linux 开发机上以 \`install.sh\` 一键安装 Bridge
- 表单字段：
  - **工作区名称**（必填）
  - **描述**（可选）
  - **Agent 类型**：Hermes（推荐）/ CodeBuddy
- 点「**创建工作区**」后弹出 Bridge 安装命令弹窗，复制命令到目标机执行即可
- Bridge 上线后，工作区自动出现在列表且状态变绿

> 暂不支持 Windows 系统。Windows 用户请改用 AnyDev 容器形态。

### 3）共享范围

- 默认仅自己可见（卡片左下角显示「仅自己」灰色 tag）
- 在工作区卡片左下角点 **铅笔编辑 icon** 进入共享范围 Modal
- 两级权限：
  - **可管理**：管理工作区配置、文件、Skill、成员权限
  - **可使用**：只读 + 可通过智能体调用
- 支持按 RTX 搜索员工，批量邀请
- 编辑后卡片 tag 自动切换为「团队可见」（绿色）

### 4）卡片操作

- **预览**：查看工作区运行状态与基础信息
- **编辑**：修改名称 / 描述（不再涉及 Agent 字段）
- **共享**：左下铅笔 icon
- **连通性测试**：右下按钮

---

## 2. 连接器

连接器合并了原 **MCP Server + Channels** 两个概念，所有数据源与通知通道在统一网格中管理。

### 1）默认连接（自动）

| 连接器 | 说明 |
|---|---|
| **CrashSight MCP** | 创建工作区时自动连接，提供崩溃数据访问能力，无需配置 |

### 2）快捷接入（OAuth 一键授权）

| 连接器 | 接入方式 | 用途 |
|---|---|---|
| **工蜂 Git** | OAuth 跳转 | 让 Agent 读取工蜂代码做归因分析 |
| **TAPD** | OAuth 跳转 | 让 Agent 自动创建 Bug 单 |

点击「**快捷授权**」按钮 → 新窗口跳转授权 → 回调后卡片状态变为「已连接」。

### 3）通知通道

#### 企业微信智能机器人

点击「**接入**」打开**双 Tab 配置弹窗**，按需选择：

- **扫码绑定**：弹出二维码，用企业微信扫码即可在企微管理后台创建并绑定智能机器人
- **手动填写**：填入 Bot ID 与 Secret，系统将在 5-8 秒内重启机器人服务并生效

> 与"绑定群聊 webhook"不同，此处创建的是企微平台侧的智能机器人，可主动推送崩溃告警与摘要。

#### 邮件通知

填写收件人邮箱列表（支持多个），完成保存。

#### 自定义 Webhook

填写 Webhook URL，CrashClaw 会在崩溃告警触发时 POST 到该地址。

### 4）自定义 MCP Server

如需接入未列出的 MCP 服务，可点击连接器页右上角「**+ 添加自定义 MCP Server**」：

| 字段 | 必填 | 说明 |
|---|---|---|
| 名称 | 是 | 自定义 MCP 标识，例如 \`my-gitlab-mcp\` |
| 描述 | 否 | 简要说明 |
| 连接方式 | 是 | SSE / Streamable HTTP / STDIO 三选一 |
| 服务配置 | 是 | JSON 格式，包含 url / headers / command / args 等字段 |

配置完成后可点弹窗左下角「**测试连接**」按钮验证可用性：

- 成功：显示 latency、Server 名称/版本、可用 tool 列表
- 失败：显示具体错误信息

测试通过后再点「确定」保存。STDIO 类型仅做配置校验，不做远程联通测试。

### 5）管理已有连接器

每张卡片底部支持：刷新、编辑、删除、启停 Toggle 开关。已连接的卡片为浅橙底 + 橙色边框，未接入的为白底浅灰边框。

---

## 3. 技能

技能页面分两个分区，按是否平台官方区分：

### 1）平台技能（默认开启，不可关闭）

CrashSight 团队维护，所有用户开箱即用。当前内置：

- 崩溃归因
- 堆栈解析
- 版本对比
- 相似聚合

> 这些技能的「停用」按钮置灰，鼠标悬停会提示「平台技能不可关闭」。如需禁用某项能力，请联系 CrashSight 团队。

### 2）自定义技能（用户上传 / 可启停 / 可删除）

点击右上角「**+ 上传 Skill**」打开上传弹窗：

| 字段 | 必填 | 说明 |
|---|---|---|
| 名称 | 是 | 技能标识 |
| Skill 文件 | 是 | 支持 \`.zip\`（包含 \`SKILL.md\`）/ 单个 \`SKILL.md\` / 文件夹拖拽 |
| 使用说明 | 否 | Markdown 描述 |
| 触发条件 | 否 | 每次崩溃分析自动执行 / 手动触发 / 满足条件时触发 |

上传后默认为「停用」状态，需手动启用。已启用的技能卡片为绿色边框，停用的为灰色。

每个自定义技能卡片底部显示上传者、更新时间，支持编辑 / 启停 / 删除。

---

## 4. Agent

v2 起 Agent 完全由后端托管，前端不再暴露 Agent 配置 Tab。

- 创建工作区时，后端会自动绑定一个默认 Agent
- 编辑工作区时，原有 Agent 关联保持不变
- 默认 Agent 已组合了平台技能 + 当前工作区已接入的连接器
- 如有定制 Agent 需求（如自定义 System Prompt、特殊模型组合），请联系 CrashSight 团队

---

## 常见问题

**Q：本地没有 Mac/Linux 开发机，但需要在自己机器上跑 CrashClaw？**
A：选 AnyDev 容器形态，由系统在云端拉起一个开发容器，效果等同。

**Q：我创建了 CVM 工作区，安装命令复制了但忘了用，能找回吗？**
A：Bridge token 24 小时内有效。可重新打开「创建工作区」→「CVM / 开发机」Tab，重新填写同名称即可生成新命令；过期 token 不会污染数据。

**Q：连接器里没有我需要的服务，怎么接入？**
A：用「+ 添加自定义 MCP Server」配置任意 MCP 协议的服务。如果该服务本身不是 MCP，可参考 [MCP 自定义 Server 开发指南](https://modelcontextprotocol.io/docs) 自行包一层。

**Q：我把平台技能关掉了，是否影响别的工作区？**
A：平台技能无法被任何用户关闭，所有工作区共享一份。
`;
const CrashClawDocs = () => (_jsxs("div", { className: style.crashClawDocs, children: [_jsx("div", { className: style.docsHeader, children: _jsx("h2", { children: ze('CrashClaw 说明文档', 'CrashClaw Documentation') }) }), _jsx("div", { className: style.docsContent, children: _jsx(ReactMarkdown, { remarkPlugins: [gfm], children: MARKDOWN_CONTENT, components: {
                    code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (_jsx(SyntaxHighlighter, { children: String(children).replace(/\n$/, ''), style: stackoverflowDark, language: match[1], PreTag: "div", ...props })) : (_jsx("code", { className: className, ...props, children: children }));
                    },
                    img({ node, src, alt }) {
                        return _jsx(Image, { style: { maxWidth: '720px' }, src: resolveImageSrc(src), alt: alt });
                    },
                    a({ node, children, href, ...props }) {
                        return (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer", ...props, children: children }));
                    },
                } }) })] }));
export default CrashClawDocs;
